/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// @ts-nocheck
/* eslint-disable no-unused-vars */
import React, {useEffect} from 'react';
import ReactDOM from 'react-dom';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import Dialog from '@react/react-spectrum/Dialog';
import Button from '@react/react-spectrum/Button';
import Close from '@react/react-spectrum/Icon/Close';
import getConfig from '../../../config';
import {SAPCODE_APPLINEAGE_MAPPING} from './constants';
import {getLocalizedURL} from '@intl/adobe-locales';
import AnalyticsUtils from '../../../utils/AnalyticsUtil';
import bowser from 'bowser';
import {DeviceDetectionUtil} from 'cchome-shared/utils';

/* eslint-enable no-unused-vars */
import './MiniPlanModal.scss';

let MINIPLAN_MODAL_KEY: number | null | undefined;

type Props = {
	src: string;
	onClose: Function;
	onLoad: Function;
	backdropClickable?: boolean;
};

const productTwPURL = (sapCode) => {
	switch (sapCode) {
		case 'AEFT':
			return '/aftereffects.html?mid=ft&promoid=P79NQQBG&mv=other&web=1';
		case 'FRSC':
			return '/fresco.html?mid=ft&promoid=PGRQQGVD&mv=other&web=1';
		case 'ILST':
			return '/illustrator.html?mid=ft&promoid=PLHRQC4C&mv=other&web=1';
		case 'IDSN':
			return '/indesign.html?mid=ft&promoid=PQ7SQ7DB&mv=other&web=1';
		case 'LRCC':
			return '/photoshop-lightroom.html?mid=ft&promoid=PTYTQ3N9&mv=other&web=1';
		case 'LTRM':
			return '/photoshop-lightroom-classic.html?mid=ft&promoid=PYPVPYX8&mv=other&web=1';
		case 'PHSP':
			return '/photoshop.html?mid=ft&promoid=Q3FWPV67&mv=other&web=1';
		case 'PPRO':
			return '/premiere.html?mid=ft&promoid=Q75XPQG6&mv=other&web=1';
		case 'RUSH':
			return '/video.html?mid=ft&promoid=QBWYPLQ5&mv=other&web=1';
		default:
			return '/creativecloud.html?mid=ft&promoid=PC1PQLLF&mv=other&web=1';
	}
};

export const MiniPlanModal = ({src, onClose, onLoad, backdropClickable}: Props) => {
	useEffect(() => {
		const root = document.querySelector('#root');
		if (root) {
			root.classList.add('disable-scrollbar');
		}

		return () => {
			if (root) {
				root.classList.remove('disable-scrollbar');
			}
		};
	}, []);

	return (
		<Dialog
			autoFocusButton={null}
			cancelLabel='Cancel'
			confirmDisabled={false}
			disableEscKey
			isDismissible={false}
			keyboardConfirm={false}
			id='mini-plan-modal'
			className='mini-plan-modal'
			open
			role='dialog'
			trapFocus
			onClose={onClose}
			onCancel={onClose}
		>
			<div className='iframe-holder'>
				<iframe id='mini-plan-iframe' frameBorder='0' src={src} onLoad={onLoad} />
				<Button
					variant='tool'
					icon={<Close />}
					aria-label='Close'
					onClick={onClose}
					id='mini-plan-close-button'
				/>
			</div>
		</Dialog>
	);
};

/**
 * Getter of the Mini Plan Modal
 * This will create a new RS Modal and add it to DOM
 * Example: addMiniPlanModal('PHSP', 'en')
 * @param {string} sapcode - SAP Code for the app
 * @param {string} locale
 * @param {function} onOpen - callback on open
 * @param {function} onClose - callback on close
 * @param {string} urlParams - custom query params to add to the Mini Plan URL
 * @param {function} onLoad - callback on iframe load
 */
export function addMiniPlanModal(
	sapcode: string,
	locale = 'en',
	onOpen: Function,
	onClose: Function,
	urlParams: string,
	onLoad?: Function,
): void {
	if (MINIPLAN_MODAL_KEY || !sapcode) {
		return;
	}

	const handleOnClose = () => {
		ModalContainer.hide(MINIPLAN_MODAL_KEY);
		MINIPLAN_MODAL_KEY = null;
		if (onClose && typeof onClose === 'function') {
			onClose();
		}
	};

	let src = `${getConfig('MINI_PLAN')}${productTwPURL(sapcode)}`;
	src = AnalyticsUtils.appendUrlWithAnalytics(getLocalizedURL(src, locale));
	if (urlParams) {
		src = src.concat(`&${urlParams}`);
	}

	const handleOnLoad = () => {
		if (onLoad && typeof onLoad === 'function') {
			onLoad(src);
		}
	};

	if (
		DeviceDetectionUtil.isInternetExplorer()
		|| bowser.isUnsupportedBrowser({safari: '12'}, window.navigator.userAgent)
	) {
		const a = document.createElement('a');
		a.href = src;
		a.target = '_blank';
		a.rel = 'noopener noreferrer';
		a.click();
	}
	else {
		MINIPLAN_MODAL_KEY = ModalContainer.show(
			<MiniPlanModal onLoad={handleOnLoad} onClose={handleOnClose} src={src} backdropClickable />,
			this,
		);
	}
	if (onOpen && typeof onOpen === 'function') {
		onOpen();
	}
}

export default MiniPlanModal;
